/*
 * Dry Brew — subscription + cart drawer behaviour
 * ------------------------------------------------------------------
 * §1  PDP add-to-cart price/label follows the selected selling plan
 * §2  Cart drawer flavor rail (one-tap add, queued, plan-inheriting)
 * §3  Cart drawer subscription clarity (checkout copy, switch to one-time)
 *
 * Config comes from window.theme.drybrew (rendered in snippets/head.liquid
 * from the cart_rewards metaobject — single source of truth).
 *
 * FIX (cart fix theme): updateCheckoutButton / updateCartTotals now only write
 * to the DOM when the value actually changes. Previously every write triggered
 * the drawer MutationObserver, which re-read /cart.js and wrote again — an
 * endless loop that kept re-rendering the drawer and hammering the cart API.
 */
(function () {
  'use strict';

  var DB = (window.theme && window.theme.drybrew) || {};
  var STR = DB.strings || {};
  var ROUTES = (window.theme && window.theme.routes) || {};

  /* ---------------------------------------------------------------- utils */

  function money(cents, withCurrency) {
    var t = window.theme || {};
    if (typeof t.formatMoney !== 'function') return '$' + (cents / 100).toFixed(2);
    var useCurrency = withCurrency || (t.settings && t.settings.currency_code_enable);
    return t.formatMoney(cents, useCurrency ? t.moneyWithCurrencyFormat : t.moneyFormat);
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  function json(url, options) {
    return fetch(url, options).then(function (r) {
      return r.json().then(function (body) {
        if (!r.ok) throw body;
        return body;
      });
    });
  }

  /* ============================================================== §1 PDP */

  // Guard so our own DOM writes don't re-trigger the MutationObserver.
  var writing = false;

  function selectedVariant(scope) {
    var el = scope.querySelector('variant-selects [data-selected-variant]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Seal Subscriptions drives its widget with its OWN input names
  // (subs_type_<hash> radios, a subs_interval select) and only syncs the
  // hidden input[name="selling_plan"] afterwards — so never key off the
  // selling_plan input's own events. Read its value instead.
  function selectedPlanId(scope) {
    var nodes = scope.querySelectorAll('[name="selling_plan"]');
    var fallback = null;
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.type === 'radio' || n.type === 'checkbox') {
        if (n.checked) return n.value || null;
      } else if (!fallback && n.value) {
        fallback = n.value;
      }
    }
    return fallback;
  }

  function planAllocation(variant, planId) {
    if (!variant || !planId) return null;
    var allocs = variant.selling_plan_allocations || [];
    for (var i = 0; i < allocs.length; i++) {
      if (Number(allocs[i].selling_plan_id) === Number(planId)) return allocs[i];
    }
    return null;
  }

  /*
   * The bug this replaces: the button label was bound to variant.price and only
   * re-rendered on variant change. Price now comes from the selected selling
   * plan allocation, and re-renders on plan change too.
   */
  function currentPrice(variant, planId) {
    if (!variant) return null;
    var alloc = planAllocation(variant, planId);
    if (!alloc) {
      return {
        price: variant.price,
        compare: variant.compare_at_price,
        subscribed: false
      };
    }
    var per = alloc.per_delivery_price != null ? alloc.per_delivery_price : alloc.price;
    return {
      price: per,
      // One-time price of the same variant — this is what gets struck through.
      compare: variant.price,
      subscribed: true
    };
  }

  function setButtonLabel(el, subscribed) {
    var label = subscribed
      ? (STR.subscribe || 'Subscribe')
      : (STR.addToCart || 'Add to cart');
    for (var i = 0; i < el.childNodes.length; i++) {
      var n = el.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim() !== '') {
        n.nodeValue = label + ' ';
        return;
      }
    }
    el.insertBefore(document.createTextNode(label + ' '), el.firstChild);
  }

  function renderAddToCart(scope) {
    var variant = selectedVariant(scope);
    if (!variant || !variant.available) return;

    var planId = selectedPlanId(scope);
    var info = currentPrice(variant, planId);
    if (!info) return;

    var qtyEl = scope.querySelector('[data-quantity-field]');
    var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;

    writing = true;

    var targets = scope.querySelectorAll('[data-add-to-cart-text]');
    for (var i = 0; i < targets.length; i++) {
      var target = targets[i];
      setButtonLabel(target, info.subscribed);

      // Price only lives in the button when the theme setting is on.
      if (!target.hasAttribute('data-atc-button')) continue;

      var priceEl = target.querySelector('[data-product-price]');
      var compareEl = target.querySelector('[data-compare-price]');

      if (priceEl) {
        priceEl.innerHTML = info.price === 0
          ? ((window.theme.strings && window.theme.strings.free) || 'Free')
          : money(info.price * qty);
        priceEl.classList.toggle('product__price--sale', info.compare > info.price);
      }
      if (compareEl) {
        if (info.compare > info.price) {
          compareEl.innerHTML = money(info.compare * qty);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.classList.add('hidden');
        }
      }
    }

    // Mirror onto the standalone price block so the button and the price block
    // can never disagree (acceptance criterion 4).
    var block = scope.querySelector('[data-price-wrapper]:not([data-atc-button])');
    if (block && info.subscribed) {
      var bPrice = block.querySelector('[data-product-price]');
      var bCompare = block.querySelector('[data-compare-price]');
      if (bPrice) {
        bPrice.innerHTML = money(info.price);
        bPrice.classList.add('product__price--sale');
      }
      if (bCompare && info.compare > info.price) {
        bCompare.innerHTML = money(info.compare);
        bCompare.classList.remove('hidden');
      }
    }

    requestAnimationFrame(function () { writing = false; });
  }

  var scheduleRender = debounce(function (scope) {
    if (scope) return renderAddToCart(scope);
    document.querySelectorAll('product-info').forEach(renderAddToCart);
  }, 40);

  function bindProductInfo(scope) {
    if (scope.__dbBound) return;
    scope.__dbBound = true;

    // Deliberately broad: the subscription widget is app-rendered, so we can't
    // rely on knowing which control the shopper touched. Re-rendering is cheap
    // and idempotent.
    var rerender = function () {
      scheduleRender(scope);
      // Seal syncs its hidden selling_plan input a beat after its own handler,
      // so take a second pass.
      setTimeout(function () { renderAddToCart(scope); }, 300);
    };

    scope.addEventListener('change', rerender);
    scope.addEventListener('click', rerender);

    // Last resort: the widget may set the hidden input without firing anything
    // that bubbles to us.
    if (window.MutationObserver) {
      var mo = new MutationObserver(function () {
        if (writing) return;
        scheduleRender(scope);
      });
      mo.observe(scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'checked', 'class', 'aria-checked']
      });
    }

    scheduleRender(scope);
  }

  function initProductInfo() {
    document.querySelectorAll('product-info').forEach(bindProductInfo);

    if (typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
      var e = window.theme.PUB_SUB_EVENTS;
      if (e.variantChange) window.subscribe(e.variantChange, function () { scheduleRender(); });
      if (e.quantityUpdate) window.subscribe(e.quantityUpdate, function () { scheduleRender(); });
    }

    // product-info can be swapped wholesale on a product swap.
    document.addEventListener('theme:product-info:loaded', function (ev) {
      if (ev.target && ev.target.matches && ev.target.matches('product-info')) {
        bindProductInfo(ev.target);
      }
    });
  }

  /* ==================================================== §2/§3 CART DRAWER */

  var cartState = { planId: null, planName: null, productIds: [] };

  function railRoot() { return document.querySelector('[data-db-rail]'); }

  function flavorMap() {
    var el = document.querySelector('[data-db-flavor-map]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  function readCart() {
    return json(ROUTES.cart_url + '.js', { headers: { Accept: 'application/json' } });
  }

  function applyCartState(cart) {
    cartState.planId = null;
    cartState.planName = null;
    cartState.productIds = [];

    (cart.items || []).forEach(function (item) {
      cartState.productIds.push(String(item.product_id));
      if (!cartState.planId && item.selling_plan_allocation) {
        cartState.planId = item.selling_plan_allocation.selling_plan.id;
        cartState.planName = item.selling_plan_allocation.selling_plan.name;
      }
    });

    updateRail();
    updateCheckoutButton();
    updateCartTotals(cart);
  }

  // "Hide flavors already in the cart" — matched on product, so someone who has
  // the Double Shot carton isn't offered the Double Shot 3-pack either.
  function updateRail() {
    var rail = railRoot();
    if (!rail) return;

    var map = flavorMap();
    var present = {};
    cartState.productIds.forEach(function (pid) {
      if (map[pid]) present[map[pid]] = true;
    });

    var items = rail.querySelectorAll('[data-db-rail-item]');
    var visible = 0;
    items.forEach(function (btn) {
      var hidden = !!present[btn.getAttribute('data-flavor')];
      if (btn.hidden !== hidden) btn.hidden = hidden;
      if (!hidden) visible++;
    });

    // Empty state: hide the rail entirely rather than showing an empty row.
    var railHidden = visible === 0;
    if (rail.hidden !== railHidden) rail.hidden = railHidden;
  }

  function updateCheckoutButton() {
    var btn = document.querySelector('[data-db-checkout]');
    if (!btn) return;
    var subscribed = !!cartState.planId;
    var label = subscribed
      ? (STR.subscribeCheckout || 'Subscribe & checkout')
      : (STR.checkout || 'Checkout');
    for (var i = 0; i < btn.childNodes.length; i++) {
      var n = btn.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim() !== '') {
        // Only write when it changes — a no-op write still fires the drawer
        // MutationObserver and restarts the sync loop.
        if (n.nodeValue !== label + ' ') n.nodeValue = label + ' ';
        btn.classList.toggle('cart__checkout--subscription', subscribed);
        return;
      }
    }
  }

  function setText(el, text) {
    if (el.textContent !== text) el.textContent = text;
  }

  function updateCartTotals(cart) {
    var t = window.theme || {};
    var strings = t.strings || {};
    var qty = cart.item_count;

    document.querySelectorAll('[data-cart-items-qty]').forEach(function (el) {
      var one = strings.cart_items_one || '{{ count }} item';
      var many = strings.cart_items_many || '{{ count }} items';
      setText(el, (qty === 1 ? one : many).replace('{{ count }}', qty));
      el.classList.toggle('hidden', qty === 0);
    });

    // The checkout button's money span sits OUTSIDE the region the Section
    // Rendering API refreshes, so refreshDrawer has to update it explicitly or
    // it goes stale (e.g. still showing the subscribed price after someone
    // switches that line to one-time).
    document.querySelectorAll('[data-cart-total-price]').forEach(function (el) {
      setText(el, cart.total_price === 0
        ? (strings.free || 'Free')
        : money(cart.total_price, true));
    });
  }

  // Re-render the drawer regions the theme itself re-renders, using the same
  // Section Rendering endpoint (sections/api-cart-items.liquid).
  function refreshDrawer() {
    return fetch(ROUTES.root + '?section_id=api-cart-items')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var pairs = [
          ['[data-api-line-items]', '[data-items-holder]'],
          ['[data-api-upsell-items]', '[data-pair-products-holder]'],
          ['[data-api-cart-price]', '[data-cart-price-holder]']
        ];
        pairs.forEach(function (p) {
          var src = doc.querySelector(p[0]);
          var dest = document.querySelector(p[1]);
          if (src && dest) dest.innerHTML = src.innerHTML;
        });
        document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true }));
      })
      .then(readCart)
      .then(applyCartState);
  }

  /* ------------------------------------------------------- rail add queue */

  // The Cart AJAX API drops concurrent writes, so every add goes through one
  // serial promise chain. Rapid tapping queues instead of silently losing adds.
  var queue = Promise.resolve();

  function railAdd(btn) {
    if (btn.dataset.dbBusy === 'true') return;
    btn.dataset.dbBusy = 'true';

    // Optimistic: the row disappears immediately, and comes back on failure.
    btn.hidden = true;
    btn.classList.add('is-adding');

    queue = queue.then(function () {
      var payload = { id: Number(btn.getAttribute('data-variant-id')), quantity: 1 };
      // Inherit the cart's selling plan so a subscription cart doesn't end up
      // half-recurring.
      if (cartState.planId) payload.selling_plan = Number(cartState.planId);

      return json(ROUTES.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function () { return refreshDrawer(); })
        .catch(function (err) {
          // Roll back the optimistic hide.
          btn.hidden = false;
          btn.classList.add('is-error');
          setTimeout(function () { btn.classList.remove('is-error'); }, 2500);
          console.warn('[Dry Brew] flavor rail add failed', err);
        })
        .then(function () {
          btn.classList.remove('is-adding');
          btn.dataset.dbBusy = 'false';
        });
    });
  }

  /* --------------------------------------------------- switch to one-time */

  function switchToOneTime(link) {
    var key = link.getAttribute('data-line-key');
    var qty = Number(link.getAttribute('data-quantity')) || 1;
    var variantId = Number(link.getAttribute('data-variant-id'));
    if (!key) return;

    link.classList.add('is-busy');

    queue = queue.then(function () {
      return json(ROUTES.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty, selling_plan: null })
      })
        .then(function (cart) {
          // Some subscription apps ignore a null selling_plan on change.js.
          // Fall back to remove-then-add without a plan.
          var stillSubscribed = (cart.items || []).some(function (i) {
            return i.key === key && i.selling_plan_allocation;
          });
          if (!stillSubscribed) return cart;

          return json(ROUTES.cart_change_url + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          }).then(function () {
            return json(ROUTES.cart_add_url + '.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: qty })
            });
          });
        })
        .then(function () { return refreshDrawer(); })
        .catch(function (err) {
          console.warn('[Dry Brew] switch to one-time failed', err);
        })
        .then(function () { link.classList.remove('is-busy'); });
    });
  }

  /* ------------------------------------------------------------- wiring */

  function initCart() {
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;

      var railItem = e.target.closest('[data-db-rail-item]');
      if (railItem) {
        e.preventDefault();
        railAdd(railItem);
        return;
      }
      var oneTime = e.target.closest('[data-db-switch-onetime]');
      if (oneTime) {
        e.preventDefault();
        switchToOneTime(oneTime);
      }
    });

    var sync = debounce(function () {
      readCart().then(applyCartState).catch(function () {});
    }, 120);

    // The drawer lives in a <template> until first opened, and the theme
    // re-renders the line items on its own cart updates. Re-read the plan from
    // the cart itself rather than trusting a JS variable, so the selection
    // survives Section Rendering re-renders.
    if (window.MutationObserver) {
      var drawer = document.querySelector('[data-cart-drawer]');
      if (drawer) new MutationObserver(sync).observe(drawer, { childList: true, subtree: true });
    }
    ['theme:cart:open', 'theme:cart:close', 'theme:cart:change', 'theme:cart:added'].forEach(function (evt) {
      document.addEventListener(evt, sync);
    });
    if (typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS && window.theme.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.cartUpdate, sync);
    }

    sync();
  }

  /* --------------------------------------------------------------- boot */

  function init() {
    initProductInfo();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
